'use client'

import { formatPrice } from '@/core/lib/utils/format-price'
import { Button } from '@/core/ui/atoms/button'
import { useReservationStore } from '@/features/reservation/store'
import { RefundRequestModel } from '@/features/reservation/types/models/refund-request.model'
import { RefundModel } from '@/features/reservation/types/models/refund.model'
import { useTranslations } from 'next-intl'
import { use } from 'react'

import { useMutation } from '@/core/hooks/use-mutation'
import { FormRadioGroup } from '@/core/ui/forms/radio'
import { RefundMethods } from '@/features/trips/types/enums/refund-methods.enum'
import { FormProvider, useForm } from 'react-hook-form'
import { refund } from '../../providers/client'
import { PaymentMethodRadio } from './payment-method-radio'
import { radioClassNames } from './radio-classnames'

type Props = {
  refundPromise: Promise<RefundModel>
  onPaymentSuccess: () => void
  onPaymentFail: (error: string) => void
}

export const Refund: React.FC<Props> = ({
  refundPromise,
  onPaymentFail,
  onPaymentSuccess,
}) => {
  const t = useTranslations()
  const refundMethods = use(refundPromise)
  const { createdTrip, tripToUpdate } = useReservationStore()
  const currency = createdTrip?.currency
  const tripId = createdTrip?.id
  const refundValue =
    (tripToUpdate?.totalPrice || 0) - (createdTrip?.totalPrice || 0)

  const { mutate, isPending } = useMutation({
    mutation: refund,
    onSuccess: onPaymentSuccess,
    onError: onPaymentFail,
  })

  const form = useForm<RefundRequestModel>({
    defaultValues: {
      refundMethod: RefundMethods.WALLET,
      tripId,
    },
  })

  return (
    <article>
      <h3 className='text-2xl font-medium'>{t('cancellationPolicy.refund')}</h3>
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(mutate)}
          className='my-4 flex w-full max-w-lg flex-col gap-8'
        >
          <FormRadioGroup
            control={form.control}
            name='refundMethod'
            className='w-full max-w-full'
          >
            <PaymentMethodRadio
              color='danger'
              classNames={radioClassNames()}
              value={RefundMethods.WALLET}
              bordered
            >
              {t('forms.walletRefund', {
                amount: formatPrice(
                  (refundMethods?.wallet || 0) +
                    (refundMethods?.bankAccount || 0),
                  currency,
                ),
              })}
            </PaymentMethodRadio>

            {!refundMethods?.wallet && (
              <PaymentMethodRadio
                color='danger'
                classNames={radioClassNames()}
                value={RefundMethods.BANK_ACCOUNT}
                bordered
              >
                {t('forms.originRefund', {
                  amount: formatPrice(refundMethods?.bankAccount, currency),
                })}
              </PaymentMethodRadio>
            )}
          </FormRadioGroup>
          {Boolean(refundMethods?.wallet && refundMethods?.bankAccount) && (
            <PaymentMethodRadio
              color='danger'
              classNames={radioClassNames()}
              value={RefundMethods.BOTH}
              bordered
            >
              {t('trip.cancel.toWallet', {
                walletAmount: formatPrice(refundMethods?.wallet, currency),
                bankAmount: formatPrice(refundMethods?.bankAccount, currency),
              })}
            </PaymentMethodRadio>
          )}

          <div className='flex flex-wrap items-center justify-start gap-5'>
            <p className='flex flex-col'>
              <span className='text-grey'>{t('trip.totalInc')}</span>
              <span className='text-red text-4xl leading-8 font-semibold'>
                {formatPrice(refundValue, currency)}
              </span>
            </p>
            <Button
              type='submit'
              loading={isPending}
              className='h-11 w-64 self-end px-6 sm:px-16'
            >
              {t('actions.refund')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </article>
  )
}
